import { Link } from "react-router-dom";
import {
  Shield,
  CheckCircle2,
  Users,
  Compass,
  Sparkles,
  ArrowRight,
  Handshake,
  UserCheck,
  Workflow,
  Bot,
  AlertTriangle,
  FileCheck2,
  Layers,
  ClipboardList,
  Linkedin,
  Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { TemplateDevelopmentNotice } from "@/components/TemplateTrustNotice";
import { Card, CardContent } from "@/components/ui/card";
import { usePageSEO } from "@/hooks/usePageSEO";
import founderPhoto from "@/assets/aymen-bentijani.jpg.asset.json";
import { PractitionerFeedback } from "@/components/marketing/PractitionerFeedback";

export default function About() {
  usePageSEO({
    title: "About ISO27001KIT - Practical ISO 27001 Implementation",
    description:
      "ISO27001KIT helps teams move from scattered documents to an organised, audit-ready ISMS. See our philosophy, who we serve, and where AI fits.",
    canonicalPath: "/about",
  });

  const whoItIsFor = [
    "Startups and SMBs pursuing ISO 27001:2022 for the first time",
    "ISMS managers replacing scattered Word and Excel files with a single workflow",
    "Security leads preparing for Stage 1 or Stage 2 audits",
    "Consultants delivering ISO 27001 implementations for multiple clients",
    "Internal teams maintaining certification through surveillance audits",
  ];

  const philosophy = [
    {
      title: "Audit reality over paperwork theatre",
      body: "A policy nobody follows fails an audit. We focus on documents that match how your team actually works.",
    },
    {
      title: "Evidence is the unit of work",
      body: "Every control needs proof an auditor can sample. Tools and templates are designed around what evidence each clause expects.",
    },
    {
      title: "Small steps, finished work",
      body: "Implementation moves in finished increments - scope, then risk, then SoA, then evidence - not endless half-done documents.",
    },
  ];

  const ismsFlow = [
    { label: "Scope", body: "Define what is in and out of the ISMS." },
    { label: "Risk", body: "Identify, score, and decide what to treat." },
    { label: "Controls", body: "Select Annex A controls in the SoA with justifications." },
    { label: "Evidence", body: "Show each applicable control is actually operating." },
    { label: "Internal audit", body: "Independently test that controls work." },
    { label: "Management review", body: "Close the loop with leadership on the ISMS performance." },
  ];

  const aiHelps = [
    "Explain a clause or Annex A control in plain language",
    "Draft first-pass policy wording you then review and own",
    "Suggest auditor-style questions to test a control",
    "Reword weak justifications in your Statement of Applicability",
  ];

  const humansDecide = [
    "Whether a control actually applies to your business",
    "Risk acceptance and treatment decisions",
    "Approving policies, scope, and the SoA",
    "Operating the controls every day and capturing evidence",
    "Internal audit findings and management review outcomes",
  ];

  const kitMeaning = [
    {
      icon: FileCheck2,
      title: "Practical ISO 27001 documents",
      body: "Templates are designed to support real implementation work, not just create paperwork.",
    },
    {
      icon: Layers,
      title: "Risk and SoA alignment",
      body: "The kit connects risk assessment, treatment decisions, Annex A controls, and Statement of Applicability reasoning.",
    },
    {
      icon: ClipboardList,
      title: "Audit-readiness mindset",
      body: "The workflow is built around helping teams understand what evidence they need and why it matters during audit preparation.",
    },
  ];

  return (
    <div className="min-h-screen py-12 sm:py-16">
      <div className="container mx-auto px-4 max-w-4xl space-y-14">
        {/* Hero */}
        <header>
          <div className="inline-flex items-center gap-2 bg-accent/15 border border-accent/30 text-accent rounded-full px-3 py-1 text-xs font-medium mb-4">
            <Shield className="h-3.5 w-3.5" />
            About ISO27001KIT
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground mb-4">
            From scattered documents to an audit-ready ISMS workflow.
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl">
            ISO27001KIT helps teams move from scattered documents to a more organised,
            audit-ready ISMS workflow - with templates, tools, and AI that respect what
            ISO 27001:2022 actually expects.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold">
              <Link to="/pricing">View pricing <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-accent/40 hover:border-accent hover:text-accent">
              <Link to="/readiness-check">Run readiness check</Link>
            </Button>
          </div>
        </header>

        {/* Why ISO27001KIT exists */}
        <section>
          <div className="flex items-center gap-3 mb-3">
            <Sparkles className="h-5 w-5 text-accent" />
            <h2 className="text-2xl font-bold text-foreground">Why ISO27001KIT exists</h2>
          </div>
          <div className="space-y-3 text-muted-foreground">
            <p>
              Most ISO 27001 projects start the same way: a folder full of half-finished
              policies, a risk spreadsheet that no one trusts, and a deadline from a customer
              or auditor. The standard is clear, but the path from "we should do this" to
              "we can prove we do this" is not.
            </p>
            <p>
              ISO27001KIT exists to shorten that path. We focus on the small number of
              artefacts auditors actually sample - scope, risk register, SoA, evidence,
              internal audit, management review - and make each one easier to produce, keep
              current, and defend.
            </p>
          </div>
        </section>

        {/* Who it is for */}
        <section>
          <div className="flex items-center gap-3 mb-3">
            <Users className="h-5 w-5 text-accent" />
            <h2 className="text-2xl font-bold text-foreground">Who it is for</h2>
          </div>
          <ul className="grid sm:grid-cols-2 gap-3">
            {whoItIsFor.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Implementation philosophy */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <Compass className="h-5 w-5 text-accent" />
            <h2 className="text-2xl font-bold text-foreground">Implementation philosophy</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {philosophy.map((p) => (
              <Card key={p.title} className="border-border/50">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-foreground mb-2">{p.title}</h3>
                  <p className="text-sm text-muted-foreground">{p.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* How the toolkit connects the ISMS */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <Workflow className="h-5 w-5 text-accent" />
            <h2 className="text-2xl font-bold text-foreground">
              How the toolkit connects your ISMS
            </h2>
          </div>
          <p className="text-muted-foreground mb-5">
            ISO 27001 is one connected system, not a stack of separate documents. The kit is
            structured around the same flow auditors follow:
          </p>
          <ol className="space-y-3">
            {ismsFlow.map((step, i) => (
              <li
                key={step.label}
                className="flex items-start gap-4 rounded-lg border border-border bg-card p-4"
              >
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-accent/15 text-accent border border-accent/30 flex items-center justify-center text-sm font-bold">
                  {i + 1}
                </span>
                <div>
                  <div className="font-semibold text-foreground">{step.label}</div>
                  <div className="text-sm text-muted-foreground">{step.body}</div>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* AI helps / humans decide */}
        <section className="grid sm:grid-cols-2 gap-6">
          <Card className="border-border/50">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <Bot className="h-5 w-5 text-accent" />
                <h3 className="text-lg font-semibold text-foreground">What AI helps with</h3>
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {aiHelps.map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <UserCheck className="h-5 w-5 text-accent" />
                <h3 className="text-lg font-semibold text-foreground">
                  What humans still need to decide
                </h3>
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {humansDecide.map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </section>

        {/* Founder / team placeholder */}
        <section>
          <div className="flex items-center gap-3 mb-3">
            <Handshake className="h-5 w-5 text-accent" />
            <h2 className="text-2xl font-bold text-foreground">
              The ISO 27001 practitioner behind the kit
            </h2>
          </div>

          {/* Founder card */}
          <Card className="border-border/50">
            <CardContent className="p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row gap-6">
                {/* Initials avatar (no stock photo) */}
                <img
                  src={founderPhoto.url}
                  alt="Aymen Bentijani, founder of ISO27001KIT"
                  loading="lazy"
                  className="flex-shrink-0 w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover border border-accent/30 mx-auto sm:mx-0"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-foreground">Aymen Bentijani</h3>
                  <p className="text-sm font-medium text-accent mt-0.5">
                    Founder of ISO27001KIT
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Cybersecurity GRC Consultant, ISO 27001 Lead Implementer, writer, and
                    builder of practical security tools.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <a
                      href="https://www.linkedin.com/in/aymen-bentijani/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full border border-accent/30 bg-accent/10 text-accent px-3 py-1 hover:bg-accent/20 transition-colors"
                      aria-label="Aymen Bentijani on LinkedIn"
                    >
                      <Linkedin className="h-3.5 w-3.5" />
                      LinkedIn
                    </a>
                    <a
                      href="mailto:Aymen@iso27001kit.com"
                      className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full border border-accent/30 bg-accent/10 text-accent px-3 py-1 hover:bg-accent/20 transition-colors"
                      aria-label="Email Aymen Bentijani"
                    >
                      <Mail className="h-3.5 w-3.5" />
                      Aymen@iso27001kit.com
                    </a>
                  </div>
                  <div className="mt-4 space-y-3 text-sm text-muted-foreground">
                    <p>
                      Aymen Bentijani founded ISO27001KIT to help implementers, consultants,
                      auditors, and security teams make ISO 27001 implementation more
                      practical, structured, and audit-ready.
                    </p>
                    <p>
                      With a background in cybersecurity GRC and ISO 27001 implementation,
                      Aymen focuses on turning complex compliance work into usable documents,
                      risk workflows, Statement of Applicability guidance, evidence tracking,
                      and implementation tools.
                    </p>
                    <p>
                      ISO27001KIT was built to help teams move away from scattered files,
                      unclear responsibilities, weak evidence, and last-minute audit
                      preparation - and toward a clearer ISO 27001 implementation workflow.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <TemplateDevelopmentNotice className="mt-6" headingLevel="h3" />

          {/* What this means for the kit */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-foreground mb-3">
              What this means for the kit
            </h3>
            <div className="grid sm:grid-cols-3 gap-4">
              {kitMeaning.map((item) => (
                <Card key={item.title} className="border-border/50 h-full">
                  <CardContent className="p-5 flex flex-col h-full">
                    <item.icon className="h-5 w-5 text-accent mb-3" />
                    <h4 className="font-semibold text-foreground mb-2">{item.title}</h4>
                    <p className="text-sm text-muted-foreground">{item.body}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold">
              <Link to="/tools">
                Explore the ISO 27001 Toolkit <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-accent/40 hover:border-accent hover:text-accent">
              <Link to="/readiness-check">Run the readiness check</Link>
            </Button>
          </div>
        </section>

        {/* Practitioner feedback */}
        <PractitionerFeedback
          heading="Practitioner feedback shaping the kit"
          intro="ISO27001KIT is built around practical ISO 27001 implementation problems: readiness gaps, scattered audit evidence, weak risk registers, Statement of Applicability clarity, and audit preparation. Feedback from ISO 27001, GRC, audit, and cybersecurity professionals helps shape the practical direction of the kit."
          keys={["nikhil", "felicitas", "david", "awuley", "sayed"]}
        />

        {/* Trust links */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-4">Policies and security</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { to: "/security", label: "Security", body: "How we handle data, accounts, AI use, and subprocessors." },
              { to: "/privacy-policy", label: "Privacy policy", body: "How we handle data and what we do not collect." },
              { to: "/terms-of-service", label: "Terms of service", body: "Your rights and obligations when using the kit." },
              { to: "/refund-policy", label: "Refund policy", body: "How purchases and refunds are handled." },
              { to: "/disclaimer", label: "Disclaimer", body: "Scope of the service and what it does not replace." },
            ].map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rounded-lg border border-border bg-card p-4 hover:border-accent transition-colors"
              >
                <div className="font-semibold text-foreground mb-1">{link.label}</div>
                <div className="text-sm text-muted-foreground">{link.body}</div>
              </Link>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="relative overflow-hidden bg-background border border-border p-8 rounded-lg text-center">
          <div className="absolute top-[-100px] left-[-50px] w-[300px] h-[300px] bg-accent/5 rounded-full blur-[80px] pointer-events-none" />
          <div className="relative z-10">
            <h2 className="text-2xl font-bold mb-3 text-foreground">
              Ready to make your ISMS auditable?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              Pick a plan that matches where you are, or run a free readiness check to see
              where the biggest gaps live before you commit to anything.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold">
                <Link to="/pricing">View pricing <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-accent/40 hover:border-accent hover:text-accent">
                <Link to="/readiness-check">Run readiness check</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
